import { OrbitCamera } from './utils/orbitCam.js';
import { LayerManager } from './layers/LayerManager.js';
import { CloudSpiralLayer } from './textures/CloudSpiral.js';
import { TextureTesterLayer } from './textures/GemTextureDemo.js';
import { HandSpiralLayer } from './textures/HandSpiral.js';
import { Gem } from './gem/Gem.js';
import { createModelFromQuads } from './utils/geometry.js';
import { scaleImageDown } from './utils/scaleImage.js';
import { loader } from './utils/loader.js';

p5.disableFriendlyErrors = true;

let config = {
  gem: {
    // 👇 set the dimensions of your gem
    size: 80,
    ratio: 1.5,
    gemCorner: 0.35,
    tableRatio: 0.75,
    loopCount: 3,
  },
  seed: 300, // 👈 set your custom seed for random and noise
  description: `Shimmering gemstone`, // 👈 describe your animation for screen readers
};

let loopLengthInFrames = 280; // 👈 set the length of your animation in frames
let autoCapture = false; // 👈 set to true once you are ready to export your animation

// Debug features disabled in production builds
let showTexturePreview = typeof IS_PRODUCTION !== 'undefined' ? !IS_PRODUCTION : true; // show a thumbnail of the texture (will not be captured)

/********************************/
/*    🌎 GLOBAL VARIABLES 🌍    */
/********************************/

// please do not edit those variables
let canvasSize;
let gemTexture;
let bufferSize = 2048;
let referenceSize = 1024;
let pxDensity = 2;
let gem;
let gemModel;
let debugMode = false;
let debugTexture;
let font;
let layerManager;
let gemFramebuffer;
let orbitCam;
let maxRotationAngle = Math.PI / 16;
let texScrollShader;
let baseWindowScale;
let cloudImage;
let handImage;
let cloudImageOriginal; // Store original full-size images
let handImageOriginal;

/********************************/
/*          🥃 SETUP 🥃         */
/********************************/

/**
 * Logs deployment information to console
 * Shows build date, time, and duration since deployment
 */
function logDeploymentInfo() {
  // Check if build timestamp is available (injected by webpack)
  if (typeof __BUILD_TIMESTAMP__ !== 'undefined') {
    const buildDate = new Date(__BUILD_TIMESTAMP__);
    const now = new Date();
    const durationMs = now - buildDate;

    // Format date as: Jan 12, 2025
    const dateStr = buildDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Format time as: 3:45 PM
    const timeStr = buildDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Calculate human-readable duration
    let durationStr;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      durationStr = `${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      durationStr = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      durationStr = `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else {
      durationStr = `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    }
    const tzName = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(buildDate)
      .find(part => part.type === 'timeZoneName')?.value || '';
    console.log(`📅 Last updated on ${dateStr} at ${timeStr}${tzName ? ' ' + tzName : ''} (${durationStr})`);
  }
}

async function setup() {

  console.log(`💎 Carat Clouds by Jenni Pasanen & Raphaël de Courville`);

  // Log deployment information
  logDeploymentInfo();

  canvasSize = min(windowWidth, windowHeight);

  // Determine pixel density based on device capabilities
  // This is a rough estimate; you can customize further if needed
  pxDensity = window.devicePixelRatio || 1;
  pxDensity = constrain(pxDensity, 0.5, 2); // limit to max 2 for performance
  // limit to 1 on mobile devices for performance
  if (/Mobi|Android/i.test(navigator.userAgent) || navigator.userAgentData?.mobile) {
    pxDensity = 1;
  }

  // Create initial 2D canvas for loader
  createCanvas(canvasSize, canvasSize);
  centerCanvas();

  // Start the loading animation (will auto-hide when setup completes)
  loader(true, 0); // useLogo=true, startDelay=0

  // Load assets using async/await (p5.js 2.x)
  texScrollShader = await loadShader(
    "app/shaders/scrollUV.vert",
    "app/shaders/scrollUV.frag"
  );
  font = await loadFont("./assets/fonts/JetBrainsMono-Regular.ttf");

  // Load original full-size images
  cloudImageOriginal = await loadImage("./assets/images/cloud.png");
  handImageOriginal = await loadImage("./assets/images/hand_Donene.png");

  // Generate initial scaled versions based on canvas size
  regenerateScaledImages();

  // Recreate canvas as WEBGL after assets are loaded
  createCanvas(canvasSize, canvasSize, WEBGL);
  centerCanvas();

  // General sketch setup
  pixelDensity(pxDensity);
  randomSeed(config.seed);
  noiseSeed(config.seed);
  describe(config.description);
  textFont(font);

  // Helpers - initialize OrbitCamera AFTER WEBGL context is fully ready
  // Use setTimeout to ensure WEBGL context is completely initialized
  orbitCam = new OrbitCamera(maxRotationAngle, 0.05, width, height, pxDensity);

  // Initialize layer manager with all layers
  layerManager = new LayerManager();
  layerManager.addLayer('cloudSpiral', new CloudSpiralLayer(canvasSize, canvasSize, pxDensity, cloudImage), 0);
  layerManager.addLayer('debugTexture', new TextureTesterLayer(bufferSize, bufferSize, pxDensity, false), 1); // autoResize = false for fixed size
  layerManager.addLayer('handsSpiral', new HandSpiralLayer(canvasSize, canvasSize, pxDensity, handImage), 2);
  layerManager.init(font);

  // Gem framebuffer
  gemFramebuffer = createFramebuffer({ width: canvasSize, height: canvasSize, depth: false, stencil: false });

  // Gem
  const ratio = config.gem.ratio;
  const gemWidth = config.gem.size;
  const gemCorner = config.gem.gemCorner;
  const tableRatio = config.gem.tableRatio;
  const loopCount = config.gem.loopCount;
  const gemHeight = gemWidth * ratio;
  gem = new Gem(
    gemWidth,
    gemHeight,
    gemCorner,
    loopCount,
    tableRatio,
    bufferSize
  );
  gemModel = createModelFromQuads(gem.getQuads());
}

/********************************/
/*          🎨 DRAW 🎨          */
/********************************/

function draw() {
  // precentage of the animation that is completed
  let loopPercentage = map(frameCount - 1, 0, loopLengthInFrames, 0.0, 1.0);

  // Get camera rotation
  let rotation = orbitCam.getNormalizedRotation();

  // Update all layers with current state
  layerManager.updateAll({
    loopPercent: loopPercentage,
    rotationX: rotation.x,
    rotationY: rotation.y
  });

  // Render cloudSpiral layer
  layerManager.renderLayer('cloudSpiral');

  // Render appropriate texture based on debug mode
  if (debugMode) {
    layerManager.renderLayer('debugTexture');
    debugTexture = layerManager.getBuffer('debugTexture');
  }

  // Choose texture based on debug mode (reuse cloudSpiral for gem)
  let activeTexture = debugMode ? debugTexture : layerManager.getBuffer('cloudSpiral');

  // Render handsSpiral layer
  layerManager.renderLayer('handsSpiral');

  // === RENDER GEM TO FRAMEBUFFER ===
  gemFramebuffer.begin();
  clear();

  // Bind the shader
  shader(texScrollShader);
  texScrollShader.setUniform("u_uvOffset", [orbitCam.rotY, orbitCam.rotX]);
  texScrollShader.setUniform("u_texture", activeTexture);
  texScrollShader.setUniform("u_time", millis() * 0.001);

  // Render gem with camera
  orbitCam.begin();
  push();
  noStroke();
  scale(pow(canvasSize * 0.002, 2)); // scale gem to remain proportional to canvas size
  model(gemModel);
  pop();
  orbitCam.end();

  resetShader();
  gemFramebuffer.end();

  // === COMPOSITE ALL LAYERS TO MAIN CANVAS ===
  clear();

  background("white");

  // Layer: cloudSpiral
  push();
  imageMode(CENTER);
  image(layerManager.getBuffer('cloudSpiral'), 0, 0, width, height);
  pop();

  // Layer: handsSpiral
  push();
  imageMode(CENTER);
  image(layerManager.getBuffer('handsSpiral'), 0, 0, width, height);
  pop();

  // Layer: Gem
  push();
  imageMode(CENTER);
  image(gemFramebuffer, 0, 0, width, height);
  pop();

  if (showTexturePreview) drawTexturePreview(activeTexture);
}

function drawTexturePreview(t) {
  push();
  translate(-width / 2, -height / 2);
  translate(width * 0.02, height * 0.02, 0);
  image(t, 0, 0, 100, 100);
  noFill();
  stroke("white");
  rect(0, 0, 100, 100);
  pop();
}

/********************************/
/*       INPUT HANDLERS         */
/********************************/

// Keyboard input
function keyPressed() {
  // toggle fullscreen (press F)
  if (key === 'f' || key === 'F') {
    let fs = fullscreen();
    fullscreen(!fs);
    console.log(`Fullscreen mode: ${!fs}`);
  }

  // Debug shortcuts only available in development mode
  const isProduction = typeof IS_PRODUCTION !== 'undefined' ? IS_PRODUCTION : false;
  if (!isProduction) {
    // Toggle FPS counter (press P or p) - controls stats.js visibility
    if (key === 'p' || key === 'P') {
      if (typeof stats !== 'undefined' && stats.dom) {
        const isVisible = stats.dom.style.display !== 'none';
        stats.dom.style.display = isVisible ? 'none' : 'block';
        console.log(`FPS counter visible: ${!isVisible}`);
      }
    }

    // Toggle debug mode with 'd' or 'D'
    if (key === 'd' || key === 'D') {
      debugMode = !debugMode;
      console.log('Debug mode:', debugMode ? 'ON (GemTextureDemo)' : 'OFF (Gem Texture)');
    }

    // Toggle texture preview with 't' or 'T'
    if (key === 't' || key === 'T') {
      showTexturePreview = !showTexturePreview;
      console.log('Texture Preview:', showTexturePreview ? 'ON' : 'OFF');
    }
  }
}

// Mouse input
function mousePressed() {
  orbitCam.mousePressed(mouseX, mouseY);
}

function mouseDragged() {
  orbitCam.mouseDragged(mouseX, mouseY);
}

function mouseReleased() {
  orbitCam.mouseReleased();
}

// Responsive canvas
function centerCanvas() {
  var s = document.body.style;
  s.display = "flex";
  s.overflow = "hidden";
  s.height = "100vh";
  s.alignItems = "center";
  s.justifyContent = "center";
}

// Debounce timer for resize
let resizeTimeout;
const RESIZE_DEBOUNCE_MS = 250; // Wait 250ms after resize stops

function windowResized() {
  // Stop the draw loop during resize
  noLoop();

  // Hide canvas to show grey square behind it
  const canvas = document.querySelector('.p5Canvas');
  if (canvas) {
    canvas.classList.add('resizing');
  }

  canvasSize = min(windowWidth, windowHeight);
  resizeCanvas(canvasSize, canvasSize);
  centerCanvas();

  // Clear existing timeout
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  // Set new timeout to resize layers after resize stops
  resizeTimeout = setTimeout(() => {
    // Regenerate scaled images based on new canvas size
    regenerateScaledImages();

    // Resize all layers
    if (layerManager) {
      layerManager.resizeAll(canvasSize, canvasSize);
    }

    // Resize gem framebuffer
    if (gemFramebuffer) {
      gemFramebuffer.resize(canvasSize, canvasSize);
    }

    // Show canvas again
    if (canvas) {
      canvas.classList.remove('resizing');
    }

    // Notify orbit camera of resize
    if (orbitCam) {
      orbitCam.resized(canvasSize, canvasSize, pxDensity);
    }

    // Restart the draw loop
    loop();
  }, RESIZE_DEBOUNCE_MS);
}

/**
 * Regenerates scaled images based on current canvas size
 * Called after window resize to maintain optimal texture sizes
 */
function regenerateScaledImages() {
  // Scale based on canvas size
  const maxCloudSize = canvasSize * 1.2;
  const maxHandSize = canvasSize * 0.5;

  cloudImage = scaleImageDown(cloudImageOriginal, maxCloudSize);
  handImage = scaleImageDown(handImageOriginal, maxHandSize);
}

// Make p5.js callbacks available globally for p5 to find them
window.setup = setup;
window.draw = draw;
window.keyPressed = keyPressed;
window.mousePressed = mousePressed;
window.mouseDragged = mouseDragged;
window.mouseReleased = mouseReleased;
window.windowResized = windowResized;